import * as _ from 'lodash';
import { diagnostics } from '@blend/ui-common';

import * as queryString from 'query-string';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import States from 'login-constants/states';
import store from 'login-store';
import { setInitialLoanType } from 'login-actions/onboardingActions';
import * as featureFlags from './featureFlags';
import * as onboardingForm from './onboardingForm';
import * as sessionUtils from './sessionUtils';
import * as userUtils from './userUtils';
import { goBackIfDeploymentConfigNotEnabled } from './routingUtils';

const goBackIfSignupDisabled = goBackIfDeploymentConfigNotEnabled(['allowSignup']);

function getLastUrl() {
  const state = store.getState() as ReduxState;
  return state.global.lastRoute;
}

function redirect(path, message, nextState, replace) {
  const redirectPath = `/${path}${nextState.location.search}`;
  diagnostics.breadcrumb(message);
  diagnostics.breadcrumb(`Redirecting to /${path}`);
  replace(redirectPath);
}

export function getSupportedLoanTypes(): LoanType[] {
  const { supportedLoanTypes, enableConstructionLoans } = deploymentConfig.get();
  return _.compact([...supportedLoanTypes, enableConstructionLoans ? 'CONSTRUCTION' : null]);
}

const personalLoanCategorySet = new Set(['PERSONAL_LOAN', 'PERSONAL_LOC']);

export function getSupportedLoanTypesAndCategories(): (LoanType | LoanTypeCategory)[] {
  const supportedLoanTypes = getSupportedLoanTypes();
  const filteredLoanTypes = _.filter(supportedLoanTypes, loanType => !personalLoanCategorySet.has(loanType));
  const enablePersonalLoanTypeCategory = _.size(supportedLoanTypes) !== _.size(filteredLoanTypes);
  return _.compact([...filteredLoanTypes, enablePersonalLoanTypeCategory ? 'PERSONAL' : null]);
}

const loanTypesRequiringAdditionalSignupParameters = new Set(['PERSONAL', 'AUTO', 'SPECIALTY_VEHICLE', 'CREDIT_CARD']);
export function loanTypeRequiresAdditionalSignupParameters(loanType: LoanType | LoanTypeCategory): boolean {
  return loanTypesRequiringAdditionalSignupParameters.has(loanType);
}

function supportsMultipleLoanTypes(): boolean {
  return _.size(getSupportedLoanTypes()) > 1;
}

function hasNullOrInvalidLoanType(query) {
  return _.isNil(query.loanType) || !_.includes(deploymentConfig.get().supportedLoanTypes, _.toUpper(query.loanType));
}

function hasNoApplicationTemplateParams(query) {
  return _.isEmpty(_.pick(query, ['applicationTemplateId', 'displayLink']));
}

export function storeInitialLoanType(query) {
  const state = store.getState() as ReduxState;
  let loanType;
  if (!_.isUndefined(state.onboarding.loanType)) {
    return;
  }

  if (supportsMultipleLoanTypes()) {
    loanType = hasNullOrInvalidLoanType(query) ? null : query.loanType;
  } else {
    loanType = _.first(getSupportedLoanTypes());
  }

  if (loanType) {
    store.dispatch(setInitialLoanType(loanType));
  }
}

// We don't support short form when onboardingMilestone2_26072018 ff is off or when NTB is enabled.
// Users can only fill out short form if their propertySearchType is NOT_STARTED
export function isShortFormEligible(propertySearchType: string) {
  const { showUpFrontRatesInJourneyChooser } = deploymentConfig.get();
  const validPropertySearchTypes = ['NOT_STARTED'].concat(
    showUpFrontRatesInJourneyChooser && featureFlags.isEnabled('upfrontRatesInLogin20190117') ? 'REFINANCE' : [],
  );
  return (
    featureFlags.isEnabled('onboardingMilestone2_26072018') &&
    _.includes(validPropertySearchTypes, propertySearchType) &&
    !deploymentConfig.isNewToBank()
  );
}

export function signupOnEnter(nextState, replace) {
  const query = queryString.parse(nextState.location.search);
  const nextURL = nextState.location.pathname;
  const lastUrl = getLastUrl();

  // go back if the Allow Signup config is disabled
  goBackIfSignupDisabled(nextState);

  // save query params to the session in case they get nuked
  sessionUtils.saveQueryToSession(query);
  storeInitialLoanType(query);
  const state = store.getState() as ReduxState;
  const loanType = onboardingForm.getLoanTypeFromState();
  const initialLoanType = onboardingForm.getInitialLoanType(state);

  if (hasNoApplicationTemplateParams(query) && lastUrl !== `/${States.CHOOSE_LOAN_TYPE}` && !initialLoanType) {
    // redirect to /choose-loan-type if multiple loans are supported, the previous url was not /choose-loan-type, and
    // there's no initial loan type
    redirect(
      States.CHOOSE_LOAN_TYPE,
      `Redirecting from ${nextURL} to /${States.CHOOSE_LOAN_TYPE} because no loan type is chosen.`,
      nextState,
      replace,
    );
  } else if (initialLoanType === 'AUTO' || loanType === 'AUTO') {
    // redirect to /auto-loan if ?loanType=AUTO is given or AUTO was selected in the chooser screen,
    // regardless of feature flags
    redirect(
      States.AUTO_LOAN,
      `Redirecting from ${nextURL} to /${States.AUTO_LOAN} because initialLoanType is AUTO`,
      nextState,
      replace,
    );
  } else if (initialLoanType === 'SPECIALTY_VEHICLE' || loanType === 'SPECIALTY_VEHICLE') {
    redirect(
      States.SPECIALTY_VEHICLE_CATEGORY,
      `Redirecting from ${nextURL} to /${States.SPECIALTY_VEHICLE_CATEGORY} because initialLoanType or loanType is SPECIALTY_VEHICLE`,
      nextState,
      replace,
    );
  } else if (loanType === 'PERSONAL') {
    redirect(
      States.PERSONAL_LOAN_TYPE,
      `Redirecting from ${nextURL} to /${States.PERSONAL_LOAN_TYPE} because loanType is PERSONAL`,
      nextState,
      replace,
    );
  } else if (initialLoanType === 'CREDIT_CARD' || loanType === 'CREDIT_CARD') {
    redirect(
      States.CREDIT_CARD_PRODUCT,
      `Redirecting from ${nextURL} to /${States.CREDIT_CARD_PRODUCT} because loanType is CREDIT_CARD`,
      nextState,
      replace,
    );
  } else if (!featureFlags.isEnabled('onboardingMilestone1_09072018')) {
    // redirect to /create-account if Onboarding Milestone 1 FF is off
    // because Milestone 1 gates Onboarding features
    redirect(
      States.CREATE_ACCOUNT,
      `Redirecting from ${nextURL} to /${States.CREATE_ACCOUNT} because Onboarding Milestone 1 FF is off.`,
      nextState,
      replace,
    );
  } else if (!featureFlags.isEnabled('onboardingMilestone2_26072018')) {
    // redirect to /milestones if Onboarding Milestone 2 FF is OFF
    // because /journey is a Milestone 2 feature
    redirect(
      States.MILESTONES,
      `Redirecting from ${nextURL} to /${States.MILESTONES} because Onboarding Milestone 2 FF is OFF`,
      nextState,
      replace,
    );
  } else {
    // Onboarding Milestone 2 is ON and there's a loanType
    switch (loanType) {
      case 'MORTGAGE':
        // redirect to /journey if loanType is MORTGAGE
        redirect(
          States.JOURNEY,
          `Redirecting from ${nextURL} to /${States.JOURNEY} because Onboarding Milestone 2 FF is ON`,
          nextState,
          replace,
        );
        break;
      default:
        // redirect to /milestones if the loanType is not MORTGAGE
        // because only Mortgage loans go through Onboarding
        redirect(
          States.MILESTONES,
          `Redirecting from ${nextURL} to /${States.MILESTONES} because the loanType is ${loanType}, which is not compatible with the short form.`,
          nextState,
          replace,
        );
    }
  }
  // behavior undefined if Onboarding Milestone 2 FF is ON and Onboarding Milestone 1 FF is OFF
}

export function journeyOnEnter(nextState, replace) {
  const nextURL = nextState.location.pathname;
  const loanType = onboardingForm.getLoanTypeFromState();

  // go back if the Allow Signup config is disabled
  goBackIfSignupDisabled(nextState);

  if (!featureFlags.isEnabled('onboardingMilestone1_09072018')) {
    // redirect to /signup if Milestone 1 is OFF
    // because /journey is an Onboarding feature
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 1 FF is OFF`,
      nextState,
      replace,
    );
  } else if (!featureFlags.isEnabled('onboardingMilestone2_26072018')) {
    // redirect to /signup if Milestone 2 is OFF
    // because /journey is an Onboarding feature
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 2 FF is OFF`,
      nextState,
      replace,
    );
  } else if (loanType !== 'MORTGAGE') {
    // redirect to /signup if the loanType is not MORTGAGE
    // because only Mortgage loans are compatible with the short form
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because the loanType is ${loanType}, which is not compatible with the short form.`,
      nextState,
      replace,
    );
  }
}

export function shortFormOnEnter(nextState, replace) {
  const nextURL = nextState.location.pathname;
  const state = store.getState() as ReduxState;
  const loanType = onboardingForm.getLoanTypeFromState();
  const propertySearchType = onboardingForm.getPropertySearchType(state);
  const lastUrl = getLastUrl();
  const mortgageValidLastUrls = _.compact([
    `/${States.JOURNEY}`,
    `/${States.JOURNEY_CHOOSER}`,
    `/${States.MILESTONES}`,
    featureFlags.isEnabled('upfrontRatesInLogin20190117') ? `/${States.UPFRONT_RATES}` : null,
  ]);

  // go back if the Allow Signup config is disabled
  goBackIfSignupDisabled(nextState);

  /* istanbul ignore else: (UI-5482) */
  if (
    !featureFlags.isEnabled('onboardingMilestone1_09072018') ||
    !featureFlags.isEnabled('onboardingMilestone2_26072018')
  ) {
    // redirect to /signup if Milestone 1 or Milestone 2 is OFF
    // because short form is an Onboarding feature
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 1 FF or Onboarding Milestone 2 FF is OFF`,
      nextState,
      replace,
    );
  } else if (!_.includes(['MORTGAGE'], loanType)) {
    // Onboarding Milestone 2 FF is ON and loanType is not MORTGAGE
    // redirect to /signup because short form is not valid for other loan types
    // (don't allow getting here manually)
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 2 is ON and the loanType is ${loanType}, which is not compatible with the short form`,
      nextState,
      replace,
    );
  } else if (loanType === 'MORTGAGE') {
    // Onboarding Milestone 2 FF is ON and loanType is MORTGAGE
    if (propertySearchType !== 'NOT_IN_CONTRACT' && !_.includes(mortgageValidLastUrls, lastUrl)) {
      // redirect to /signup if the propertySearchType is not NOT_IN_CONTRACT and the last url was not in validLastUrls
      // (don't allow getting here manually to make sure the user fills out the form)
      redirect(
        States.SIGNUP,
        `Redirecting from ${nextURL} to /${
          States.SIGNUP
        } because Onboarding Milestone 2 is ON, the loanType is MORTGAGE, and the propertySearchType is ${propertySearchType} but the last url was not ${mortgageValidLastUrls.join(
          ' or ',
        )}.`,
        nextState,
        replace,
      );
    } else if (propertySearchType === 'NOT_IN_CONTRACT') {
      // redirect to /signup if the propertySearchType is NOT_IN_CONTRACT
      // because NOT_IN_CONTRACT does not use the short form
      // (don't allow getting here manually)
      redirect(
        States.SIGNUP,
        `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 2 is ON, the loanType is MORTGAGE, and the propertySearchType is ${propertySearchType}`,
        nextState,
        replace,
      );
    }
  }
}

export function upfrontRatesOnEnter(nextState, replace) {
  goBackIfSignupDisabled(nextState);

  const nextURL = nextState.location.pathname;
  const redirectQuery = queryString.parse(nextState.location.search);
  const validLastUrls = [`/${States.SHORT_FORM}`, `/${States.MILESTONES}`];
  const lastUrl = getLastUrl();

  if (
    !featureFlags.isEnabled('upfrontRatesInLogin20190117') ||
    (!_.includes(validLastUrls, lastUrl) && _.isEmpty(redirectQuery))
  ) {
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${
        States.SIGNUP
      } because Upfront Rates in Login FF is OFF or the last url was not ${validLastUrls.join(
        ' or ',
      )} or the redirection request from outside of Blend is incorrect.`,
      nextState,
      replace,
    );
  }
}

export function milestonesOnEnter(nextState, replace) {
  const nextURL = nextState.location.pathname;
  const state = store.getState() as ReduxState;
  const loanType = onboardingForm.getLoanType(state);
  const timelineValue = onboardingForm.getTimelineValue(state);
  const lastUrl = getLastUrl();
  const mortgageValidLastUrls = [
    featureFlags.isEnabled('upfrontRatesInLogin20190117') ? `/${States.UPFRONT_RATES}` : `/${States.SHORT_FORM}`,
  ];

  // go back if the Allow Signup config is disabled
  goBackIfSignupDisabled(nextState);

  if (!featureFlags.isEnabled('onboardingMilestone1_09072018')) {
    // redirect to /signup if Onboarding Milestone 1 FF is off
    // because /milestones is an Onboarding feature
    redirect(
      States.SIGNUP,
      `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 1 is off.`,
      nextState,
      replace,
    );
  } else if (featureFlags.isEnabled('onboardingMilestone2_26072018')) {
    // Onboarding Milestone 2 FF is ON
    if (!loanType) {
      // if the milestones page is refreshed, there will be no loan type, so redirect
      // back to the beginning of the form
      redirect(
        States.SIGNUP,
        `Redirecting from ${nextURL} to /${States.SIGNUP} because Onboarding Milestone 2 is ON and there is no loan type`,
        nextState,
        replace,
      );
    } else if (
      loanType === 'MORTGAGE' &&
      isShortFormEligible(timelineValue) &&
      !_.includes(mortgageValidLastUrls, lastUrl)
    ) {
      // redirect to /signup if loanType is MORTGAGE and we can go to short-form but the last url was not valid
      // (don't allow getting here manually to make sure the user fills out the form)
      redirect(
        States.SIGNUP,
        `Redirecting from ${nextURL} to /${
          States.SIGNUP
        } because Onboarding Milestone 2 is ON, the loanType is MORTGAGE, and the borrower is short-form eligible, but the last url was not ${mortgageValidLastUrls.join(
          ' or ',
        )}.`,
        nextState,
        replace,
      );
    }
  }
}

export function journeyChooserOnEnter(nextState, replace) {
  if (!userUtils.getCurrentUser()) {
    redirect(
      States.LOGIN,
      'Redirecting from journey-chooser to root because there is no CURRENT_USER. Journey Chooser is for SAML only.',
      nextState,
      replace,
    );
  }
}
